import { reactive } from 'vue'

/*
	Tracks the on-screen keyboard so docked buttons ride above it and the tab bar hides.
	iOS Safari keeps the layout viewport and shrinks the visual viewport: --kb is the gap.
	Android Chrome (interactive-widget=resizes-content) shrinks the layout viewport instead,
	so fixed elements already sit above the keyboard; we only need to know it is open.
*/
export const keyboard = reactive({ open: false, height: 0 })

const OPEN_THRESHOLD = 120
let baselineHeight = 0

function isEditable(el) {
	if (!el) return false
	if (el.isContentEditable || el.tagName === 'TEXTAREA') return true
	if (el.tagName !== 'INPUT') return false
	return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file', 'image'].includes(el.type)
}

function update() {
	const vv = window.visualViewport
	const layoutHeight = window.innerHeight
	if (!document.activeElement || !isEditable(document.activeElement)) baselineHeight = Math.max(baselineHeight, layoutHeight)

	const overlap = vv ? Math.max(0, Math.round(layoutHeight - vv.height - vv.offsetTop)) : 0
	const shrunk = baselineHeight - layoutHeight
	const editing = isEditable(document.activeElement)
	const open = editing && (overlap > OPEN_THRESHOLD || shrunk > OPEN_THRESHOLD)

	const opened = open && !keyboard.open
	keyboard.open = open
	keyboard.height = open ? overlap : 0

	const root = document.documentElement
	root.style.setProperty('--kb', `${keyboard.height}px`)
	root.classList.toggle('kb-open', open)

	// The Dock rides up with the keyboard; keep the field being typed in clear of it.
	if (opened) setTimeout(() => document.activeElement?.scrollIntoView?.({ block: 'center', behavior: 'smooth' }), 50)
}

export function installKeyboardTracking() {
	baselineHeight = window.innerHeight
	window.visualViewport?.addEventListener('resize', update)
	window.visualViewport?.addEventListener('scroll', update)
	window.addEventListener('resize', update)
	window.addEventListener('orientationchange', () => {
		baselineHeight = 0
		setTimeout(update, 300)
	})
	document.addEventListener('focusin', () => setTimeout(update, 50))
	document.addEventListener('focusout', () => setTimeout(update, 50))
	update()
}
